import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";
import {
  ArrowRight, ArrowLeft, Target, ShieldCheck, Award, AlertTriangle,
  CheckCircle2, XCircle, Mail, Building2, User,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Each Yes is worth points that sum to exactly 100.
const QUESTIONS = [
  { q: 1, text: "When a contractor visits your website and wants to buy your product, can they request a quote or connect directly with a dealer from your website?", points: 13 },
  { q: 2, text: "Does your website capture the contractor’s contact information and what product they’re interested in before sending them to a dealer?", points: 13 },
  { q: 3, text: "When a contractor clicks “Find a Dealer,” do you know which dealer they contacted or were directed to?", points: 13 },
  { q: 4, text: "Once the opportunity reaches the dealer, can you see whether the contractor was actually contacted?", points: 13 },
  { q: 5, text: "Can you see how quickly your dealer responded to the contractor?", points: 12 },
  { q: 6, text: "Do you know whether the dealer provided a quote — and, if so, the quoted amount?", points: 12 },
  { q: 7, text: "Can you see whether the opportunity ultimately became a sale, was lost, or is still pending?", points: 12 },
  { q: 8, text: "Can you measure how many contractor opportunities your website generates and how many ultimately turn into dealer sales?", points: 12 },
];
const MAX_SCORE = QUESTIONS.reduce((s, q) => s + q.points, 0); // 100

function zoneFor(score) {
  if (score >= 90) return { key: "platinum", label: "Platinum", color: "#1A365D", accent: "bg-[#1A365D]", tone: "You’re running a best-in-class dealer opportunity engine." };
  if (score >= 70) return { key: "green",    label: "Green",    color: "#16A34A", accent: "bg-[#16A34A]", tone: "You have strong visibility — a few tweaks and you’ll be best-in-class." };
  if (score >= 40) return { key: "amber",    label: "Amber",    color: "#D97706", accent: "bg-[#D97706]", tone: "You’re capturing interest, but critical opportunities are slipping through the cracks." };
  return                { key: "red",      label: "Red",      color: "#C62828", accent: "bg-[#C62828]", tone: "You have significant blind spots between your website, dealers, and revenue." };
}

export default function Scorecard() {
  const [step, setStep] = useState("intro"); // intro | quiz | form | done
  const [answers, setAnswers] = useState({}); // { 1: 'yes' | 'no' }
  const [idx, setIdx] = useState(0);
  const [form, setForm] = useState({ contact_name: "", company_name: "", email: "", role: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const startQuiz = () => {
    trackEvent("scorecard_started", { location: "intro_cta" });
    setStep("quiz");
  };

  useEffect(() => {
    document.title = "The Dealer Opportunity Scorecard — LumberLoop USA";
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta");
      m.setAttribute("name", "description"); document.head.appendChild(m); return m;
    })();
    meta.setAttribute("content", "Take the 60-second Dealer Opportunity Scorecard. See how effectively your website turns interest into opportunities for your dealers.");
    window.scrollTo(0, 0);
  }, []);

  const rawScore = useMemo(
    () => QUESTIONS.reduce((s, q) => s + (answers[q.q] === "yes" ? q.points : 0), 0),
    [answers]
  );
  const progressPct = step === "quiz" ? Math.round((idx / QUESTIONS.length) * 100) : step === "form" ? 100 : 0;

  const pick = (choice) => {
    const cur = QUESTIONS[idx];
    setAnswers((a) => ({ ...a, [cur.q]: choice }));
    setTimeout(() => {
      if (idx < QUESTIONS.length - 1) setIdx(idx + 1);
      else setStep("form");
    }, 180);
  };

  const goBack = () => {
    if (step === "form") { setStep("quiz"); return; }
    if (idx > 0) setIdx(idx - 1);
    else setStep("intro");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contact_name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setSubmitting(true);
    const z = zoneFor(rawScore);
    const payload = {
      contact_name: form.contact_name.trim(),
      company_name: form.company_name.trim(),
      email: form.email.trim(),
      role: form.role.trim(),
      score: rawScore,
      max_score: MAX_SCORE,
      zone: z.key,
      answers: QUESTIONS.map((q) => ({
        q: q.q,
        question: q.text,
        choice: answers[q.q] === "yes" ? "Yes" : "No",
        points: answers[q.q] === "yes" ? q.points : 0,
      })),
    };
    try {
      await axios.post(`${API}/score-quiz`, payload);
      trackEvent("scorecard_completed", {
        score: rawScore,
        max_score: MAX_SCORE,
        zone: z.key,
        role: form.role || "unknown",
        // GA4 recommended monetary-adjacent params make this event
        // eligible as a conversion in Ads / Analytics without extra config
        value: rawScore,
        currency: "USD",
      });
      setResult({ score: rawScore, zone: z });
      setStep("done");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("Scorecard submit failed:", err);
      trackEvent("scorecard_submit_failed", { reason: err?.response?.status || "network" });
      toast.error("Could not send your results. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#0B1F33] text-white min-h-[calc(100vh-64px)]" data-testid="scorecard-page">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16 md:py-24">
        {/* Header */}
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/15">
          <span className="w-1.5 h-1.5 rounded-full bg-[#C62828]" style={{ animation: "sol-blink 1.4s ease-in-out infinite" }}/>
          <span className="font-mono-ll text-[10px] tracking-[0.28em] uppercase text-slate-300 font-semibold">Free Assessment · 60 Seconds</span>
        </div>
        <h1 className="font-display text-4xl md:text-6xl mt-5 leading-[1.02] !text-white">
          The <span className="text-[#C62828]">Dealer Opportunity</span> Scorecard.
        </h1>
        <p className="mt-5 text-lg text-slate-300 max-w-2xl font-body">
          See how effectively your website turns interest into opportunities for your dealers. 8 quick yes/no questions — instant score.
        </p>

        {/* Progress */}
        {(step === "quiz" || step === "form") && (
          <div className="mt-10">
            <div className="flex justify-between font-mono-ll text-[10px] tracking-widest uppercase text-slate-400 mb-2">
              <span>Question {step === "form" ? QUESTIONS.length : idx + 1} of {QUESTIONS.length}</span>
              <span>{progressPct}%</span>
            </div>
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-[#C62828] transition-all duration-300" style={{ width: `${progressPct}%` }}/>
            </div>
          </div>
        )}

        {/* Steps */}
        <div className="mt-10">
          {step === "intro" && <IntroCard onStart={startQuiz}/>}
          {step === "quiz" && (
            <QuestionCard
              key={idx}
              question={QUESTIONS[idx]}
              onPick={pick}
              onBack={goBack}
              canBack={idx > 0 || step === "quiz"}
            />
          )}
          {step === "form" && (
            <EmailForm
              form={form}
              setForm={setForm}
              onBack={goBack}
              onSubmit={submit}
              submitting={submitting}
            />
          )}
          {step === "done" && result && <ResultCard score={result.score} zone={result.zone}/>}
        </div>
      </div>
    </div>
  );
}

function IntroCard({ onStart }) {
  const items = [
    { icon: Target, text: "Discover the exact gaps between your website and your revenue." },
    { icon: ShieldCheck, text: "Benchmark yourself against manufacturers who route every lead." },
    { icon: Award, text: "Get a personalized scorecard emailed to you in under a minute." },
  ];
  return (
    <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-8" data-testid="scorecard-intro">
      <div className="grid gap-4">
        {items.map((it) => (
          <div key={it.text} className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#C62828]/15 text-[#C62828] flex items-center justify-center flex-shrink-0">
              <it.icon size={16}/>
            </div>
            <p className="text-slate-200 font-body">{it.text}</p>
          </div>
        ))}
      </div>
      <button onClick={onStart} className="btn-cta mt-8 !py-3" data-testid="scorecard-start">
        Start Assessment <ArrowRight size={16}/>
      </button>
    </div>
  );
}

function QuestionCard({ question, onPick, onBack, canBack }) {
  return (
    <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-8 md:p-10 animate-fadeIn" data-testid={`scorecard-q-${question.q}`}>
      <div className="font-mono-ll text-[10px] tracking-[0.3em] uppercase text-[#C62828]">Question {question.q}</div>
      <h2 className="font-heading !text-white text-2xl md:text-3xl mt-3 leading-snug">{question.text}</h2>
      <div className="mt-8 grid sm:grid-cols-2 gap-3">
        <button onClick={() => onPick("yes")} data-testid={`scorecard-q-${question.q}-yes`} className="group rounded-xl bg-[#C62828] hover:bg-[#8f1c1c] text-white px-6 py-4 font-heading text-lg flex items-center justify-between transition-colors">
          <span className="flex items-center gap-2"><CheckCircle2 size={20}/> Yes</span>
          <ArrowRight size={16} className="opacity-70 group-hover:translate-x-1 transition-transform"/>
        </button>
        <button onClick={() => onPick("no")} data-testid={`scorecard-q-${question.q}-no`} className="group rounded-xl bg-white/5 hover:bg-white/10 text-white border border-white/15 px-6 py-4 font-heading text-lg flex items-center justify-between transition-colors">
          <span className="flex items-center gap-2"><XCircle size={20} className="text-slate-300"/> No</span>
          <ArrowRight size={16} className="opacity-70 group-hover:translate-x-1 transition-transform"/>
        </button>
      </div>
      {canBack && (
        <button onClick={onBack} className="mt-6 inline-flex items-center gap-2 text-xs font-mono-ll tracking-widest uppercase text-slate-400 hover:text-white" data-testid="scorecard-back">
          <ArrowLeft size={12}/> Back
        </button>
      )}
    </div>
  );
}

function EmailForm({ form, setForm, onBack, onSubmit, submitting }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <form onSubmit={onSubmit} className="rounded-2xl bg-white/[0.04] border border-white/10 p-8 md:p-10 space-y-5" data-testid="scorecard-form">
      <div>
        <h2 className="font-heading !text-white text-2xl md:text-3xl leading-snug">You’re done. Where should we send your scorecard?</h2>
        <p className="mt-2 text-sm text-slate-400 font-body">Your results will also be reviewed personally by our team so we can share benchmarks specific to your setup.</p>
      </div>

      <FormField icon={User} label="Full Name *" value={form.contact_name} onChange={(v) => set("contact_name", v)} placeholder="Jane Smith" testid="scorecard-name" required/>
      <FormField icon={Mail} label="Work Email *" type="email" value={form.email} onChange={(v) => set("email", v)} placeholder="jane@yourbrand.com" testid="scorecard-email" required/>
      <FormField icon={Building2} label="Company" value={form.company_name} onChange={(v) => set("company_name", v)} placeholder="Your Manufacturer Brand" testid="scorecard-company"/>

      <div>
        <label className="block text-xs font-mono-ll tracking-widest uppercase text-slate-400 mb-2">Role</label>
        <select
          value={form.role}
          onChange={(e) => set("role", e.target.value)}
          className="w-full bg-white/5 border border-white/15 text-white rounded-lg px-4 py-3 focus:outline-none focus:border-[#C62828] font-body"
          data-testid="scorecard-role"
        >
          <option value="" className="text-slate-700">Select…</option>
          {["Manufacturer", "Dealer / Distributor", "Sales Rep", "Marketing", "Executive", "Other"].map((r) => (
            <option key={r} value={r} className="text-slate-700">{r}</option>
          ))}
        </select>
      </div>

      <div className="flex items-center justify-between pt-2">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-2 text-xs font-mono-ll tracking-widest uppercase text-slate-400 hover:text-white" data-testid="scorecard-form-back">
          <ArrowLeft size={12}/> Back
        </button>
        <button type="submit" disabled={submitting} className="btn-cta !py-3" data-testid="scorecard-submit">
          {submitting ? "Sending…" : "See My Score"} <ArrowRight size={16}/>
        </button>
      </div>

      <p className="text-[11px] text-slate-500 font-body pt-2 border-t border-white/10">
        We’ll email your scorecard to <strong className="text-slate-300">info@lumberloopusa.com</strong> and send you a personalized breakdown. No sales spam — you can unsubscribe any time.
      </p>
    </form>
  );
}

function FormField({ icon: Icon, label, value, onChange, placeholder, type = "text", testid, required }) {
  return (
    <div>
      <label className="block text-xs font-mono-ll tracking-widest uppercase text-slate-400 mb-2">{label}</label>
      <div className="relative">
        <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"/>
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          className="w-full bg-white/5 border border-white/15 text-white rounded-lg pl-9 pr-4 py-3 focus:outline-none focus:border-[#C62828] placeholder:text-slate-500 font-body"
          data-testid={testid}
        />
      </div>
    </div>
  );
}

function ResultCard({ score, zone }) {
  const zoneIcon = zone.key === "red" ? AlertTriangle : zone.key === "amber" ? Target : zone.key === "green" ? ShieldCheck : Award;
  const ZI = zoneIcon;
  return (
    <div data-testid="scorecard-result">
      <div className="rounded-2xl overflow-hidden border border-white/10" style={{ background: `linear-gradient(135deg, ${zone.color} 0%, #0B1F33 140%)` }}>
        <div className="p-8 md:p-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-white font-mono-ll text-[10px] tracking-[0.3em] uppercase">
            <ZI size={12}/> Your Zone: {zone.label}
          </div>
          <div className="mt-6 flex items-end gap-3">
            <div className="font-display text-7xl md:text-8xl !text-white tabular-nums leading-none" data-testid="scorecard-score-value">{score}</div>
            <div className="text-2xl text-white/70 mb-2">/ {MAX_SCORE}</div>
          </div>
          <p className="mt-4 text-lg text-white/90 font-body max-w-xl">{zone.tone}</p>
        </div>
      </div>

      <div className="mt-6 rounded-2xl bg-white/[0.04] border border-white/10 p-6 md:p-8">
        <h3 className="font-heading !text-white text-xl">What happens next</h3>
        <ol className="mt-4 space-y-3 text-slate-300 font-body">
          <li className="flex gap-3"><span className="font-mono-ll text-xs text-[#C62828] mt-1">01</span><span>Check your inbox — your full scorecard, personalized recommendations, and demo link are already on the way.</span></li>
          <li className="flex gap-3"><span className="font-mono-ll text-xs text-[#C62828] mt-1">02</span><span>Our team will review your answers within 1 business day and share benchmarks specific to your setup.</span></li>
          <li className="flex gap-3"><span className="font-mono-ll text-xs text-[#C62828] mt-1">03</span><span>Ready sooner? Book a 15-minute Early-Access Demo below and we&rsquo;ll walk you through exactly how LumberLoop closes your gaps.</span></li>
        </ol>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/contact" onClick={() => trackEvent("scorecard_demo_click", { score, zone: zone.key })} className="btn-cta !py-3" data-testid="scorecard-cta-demo">
            Book My 15-min Demo <ArrowRight size={16}/>
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-mono-ll tracking-widest uppercase text-slate-400 hover:text-white px-4 py-3">
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
